import React, { useEffect, useState, useRef } from "react";

import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { CSVValueProps } from "../../../../context/onBoardingContext";
import { useAppState } from "../../../../context/StateContext";
import useOnBoardingContext from "../../../../hooks/useOnBoardingContext";
import { OnboardingStage } from "../../../../models/onboarding.stage";
import { UploadCSVType, UploadValueType } from "../../../../util/enums";
import { UploadContentModal } from "../UploadContentModal";
import { GET_UPLOAD_OWN_LIST, GET_UPLOAD_SHIPEEZI_LIST } from "./getUploadValue";
import { UploadFileValue } from "./UploadFileValue";
import { ColumnsValues } from "./UploadFileValue/InputUpload/getInputColumns";

export type UploadValueProps = {
  title: string;
  description: string;
  information: string;
  value?: CSVValueProps[];
  id: UploadCSVType;
  isUploaded?: boolean;
};

type UploadFileProps = {
  uploadType: UploadValueType;
  onboardingStage?: OnboardingStage;
  onUpdateOnboardingStage?: (onboardingStage: OnboardingStage) => void;
};

export const UploadFile = ({
  uploadType,
  onboardingStage,
  onUpdateOnboardingStage,
}: UploadFileProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    isOpenUploadModal,
    setIsOpenUploadModal,
    setUploadedDataCSV,
    currentColumnType,
    setCurrentColumnType,
  } = useOnBoardingContext();

  const [uploadFileOwnList, setUploadFileOwnList] = useState<UploadValueProps[]>(
    GET_UPLOAD_OWN_LIST(translate, currentUser?.company)
  );
  const uploadFileShipeeziList = GET_UPLOAD_SHIPEEZI_LIST(translate);

  // Map to store input refs for each column type to allow triggering file selection
  const inputRefsMap = useRef<Map<UploadCSVType, HTMLInputElement | null>>(new Map());

  useEffect(() => {
    if (onboardingStage) checkFilesUploaded();
  }, [onboardingStage]);

  const checkFilesUploaded = () => {
    if (onboardingStage && uploadFileOwnList) {
      let uploadFileOwnListUpdated = [...(uploadFileOwnList || [])];

      uploadFileOwnListUpdated = uploadFileOwnListUpdated.map((uploadFileOwn) => {
        switch (uploadFileOwn?.id) {
          case UploadCSVType.INVENTORY_LIST:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.inventoryWasUploaded,
            } as UploadValueProps;
          case UploadCSVType.FLEET:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.fleetWasUploaded,
            } as UploadValueProps;

          case UploadCSVType.MATERIALS:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.materialWasUploaded,
            } as UploadValueProps;

          case UploadCSVType.SERVICES:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.serviceWasUploaded,
            } as UploadValueProps;

          case UploadCSVType.STAFF:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.staffWasUploaded,
            } as UploadValueProps;

          case UploadCSVType.INSTRUCTIONS:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.instructionWasUploaded,
            } as UploadValueProps;

          case UploadCSVType.ACCESS:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage?.accessWasUploaded,
            } as UploadValueProps;
          case UploadCSVType.SHIPMENT:
            return {
              ...uploadFileOwn,
              isUploaded: onboardingStage.shipmentWasUploaded,
            } as UploadValueProps;
          // Individual Inventory categories - for now, use inventoryWasUploaded as indicator
          case UploadCSVType.INVENTORY_LOCATION:
          case UploadCSVType.INVENTORY_PACKAGE_TYPE:
          case UploadCSVType.INVENTORY_PREPARATION_METHOD:
          case UploadCSVType.INVENTORY_MODE:
          case UploadCSVType.INVENTORY_EXCEPTION:
          case UploadCSVType.INVENTORY_EXCEPTION_LOCATION:
            return {
              ...uploadFileOwn,
              isUploaded: false, // TODO: Add specific flags to onboardingStage if needed
            } as UploadValueProps;
          default:
            return uploadFileOwn;
        }
      });
      setUploadFileOwnList(uploadFileOwnListUpdated);
    }
  };

  const updateOnboardingStage = async (uploadCSVType: UploadCSVType) => {
    switch (uploadCSVType) {
      case UploadCSVType.INVENTORY_LIST:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), inventoryWasUploaded: true });
        break;
      case UploadCSVType.FLEET:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), fleetWasUploaded: true });
        break;
      case UploadCSVType.SERVICES:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), serviceWasUploaded: true });
        break;
      case UploadCSVType.INSTRUCTIONS:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), instructionWasUploaded: true });
        break;
      case UploadCSVType.MATERIALS:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), materialWasUploaded: true });
        break;
      case UploadCSVType.STAFF:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), staffWasUploaded: true });
        break;
      case UploadCSVType.ACCESS:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), accessWasUploaded: true });
        break;
      case UploadCSVType.SHIPMENT:
        if (onUpdateOnboardingStage)
          onUpdateOnboardingStage({ ...(onboardingStage || {}), shipmentWasUploaded: true });
        break;
      default:
    }
  };

  const onRequestSuccess = () => {
    setIsOpenUploadModal(false);
    setUploadedDataCSV([]);
    setCurrentColumnType(undefined);
  };

  const handleChangeUploadFile = async (value: CSVValueProps[], column: ColumnsValues) => {
    const uploadFileOwnListUpdated = await Promise.all(
      uploadFileOwnList.map(async (uploadFile) => {
        if (uploadFile.id === column) {
          await updateOnboardingStage(uploadFile?.id);

          return { ...uploadFile, value, isUploaded: true };
        }

        return uploadFile;
      })
    );

    setUploadFileOwnList(uploadFileOwnListUpdated);
  };

  const registerInputRef = (columnType: UploadCSVType, ref: HTMLInputElement | null) => {
    if (ref) {
      inputRefsMap.current.set(columnType, ref);
    } else {
      inputRefsMap.current.delete(columnType);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {uploadType === UploadValueType.UPLOAD_VALUE_OWN_LIST ? (
        <Grid container columnSpacing={6} rowSpacing={2}>
          {uploadFileOwnList?.map((uploadFileOnwListValue) => (
            <Grid item xs={12} md={6} key={uploadFileOnwListValue.title}>
              <UploadFileValue
                columnType={uploadFileOnwListValue.id}
                title={uploadFileOnwListValue.title}
                description={uploadFileOnwListValue.description}
                information={uploadFileOnwListValue.information}
                uploadType={uploadType}
                isUploaded={uploadFileOnwListValue?.isUploaded}
                uploadFile={uploadFileOnwListValue.value}
                onRegisterInputRef={(ref) => registerInputRef(uploadFileOnwListValue.id, ref)}
                onChangeFile={(file, column) => {
                  handleChangeUploadFile(file, column);
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {uploadFileShipeeziList?.map((uploadFileShipeeziListValue) => (
            <Grid item xs={12} md={6} key={uploadFileShipeeziListValue.title}>
              <UploadFileValue
                columnType={uploadFileShipeeziListValue.id}
                title={uploadFileShipeeziListValue.title}
                description={uploadFileShipeeziListValue.description}
                uploadType={uploadType}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <UploadContentModal
        isOpen={isOpenUploadModal}
        onClickTryAgain={() => {
          // Clear the failed upload attempt
          setIsOpenUploadModal(false);
          setUploadedDataCSV([]);
          setCurrentColumnType(undefined);

          // Trigger file selection for retry
          const inputRef = inputRefsMap.current.get(currentColumnType as UploadCSVType);
          if (inputRef) {
            inputRef.click();
          }
        }}
      />
    </Box>
  );
};
